import React, { useState, useEffect } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    defaultDropAnimationSideEffects,
    DragStartEvent,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    rectSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import DesktopIcon from './DesktopIcon';
import api from '../../services/api';

/**
 * Props for the DesktopGrid component
 */
interface DesktopGridProps {
    /** Array of icon configurations to display */
    icons: {
        /** Unique identifier for the icon */
        id: string;
        /** Display label for the icon */
        label: string;
        /** React node to render as the icon */
        icon: React.ReactNode;
        /** Navigation path when icon is clicked */
        path: string;
    }[];
    /** User's saved layout preferences */
    userPreferences?: {
        desktopLayout?: string[];
        mobileLayout?: string[];
    };
    /** Callback when the layout changes */
    onLayoutChange?: (newLayout: string[]) => void;
}

const SortableIcon = ({ id, icon, label, path }: { id: string, icon: React.ReactNode, label: string, path: string }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        touchAction: 'none', // Prevent scrolling while dragging on touch devices
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <DesktopIcon id={id} label={label} icon={icon} path={path} />
        </div>
    );
};

/**
 * Desktop grid component that displays draggable/sortable application icons.
 * Supports saving custom layouts and responsive design for mobile/desktop.
 *
 * @param props - The props for the component
 * @returns The Desktop Grid UI
 */
const DesktopGrid: React.FC<DesktopGridProps> = ({ icons, userPreferences, onLayoutChange }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const [items, setItems] = useState(icons);
    const [activeId, setActiveId] = useState<string | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Require 8px movement before drag starts (prevents accidental drags on click)
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Initialize order based on preferences
    useEffect(() => {
        const savedLayout = isMobile ? userPreferences?.mobileLayout : userPreferences?.desktopLayout;

        if (savedLayout && savedLayout.length > 0) {
            const sortedIcons = [...icons].sort((a, b) => {
                const indexA = savedLayout.indexOf(a.id);
                const indexB = savedLayout.indexOf(b.id);

                // If both are in layout, sort by index
                if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                // If only A is in layout, it comes first
                if (indexA !== -1) return -1;
                // If only B is in layout, it comes first
                if (indexB !== -1) return 1;
                // If neither, keep original order (or append to end)
                return 0;
            });
            setItems(sortedIcons);
        } else {
            setItems(icons);
        }
    }, [icons, userPreferences, isMobile]);

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id as string);
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setItems((items) => {
                const oldIndex = items.findIndex((item) => item.id === active.id);
                const newIndex = items.findIndex((item) => item.id === over.id);
                const newItems = arrayMove(items, oldIndex, newIndex);

                // Save new layout
                const newLayoutIds = newItems.map(item => item.id);
                saveLayout(newLayoutIds);

                return newItems;
            });
        }
        setActiveId(null);
    };

    const saveLayout = async (layoutIds: string[]) => {
        try {
            const payload = isMobile
                ? { preferences: { mobileLayout: layoutIds } }
                : { preferences: { desktopLayout: layoutIds } };

            await api.put('/api/users/profile', payload);
            if (onLayoutChange) onLayoutChange(layoutIds);
        } catch (err) {
            console.error("Error saving layout", err);
        }
    };

    const activeItem = items.find(item => item.id === activeId);

    const dropAnimation = {
        sideEffects: defaultDropAnimationSideEffects({
            styles: {
                active: {
                    opacity: '0.5',
                },
            },
        }),
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'row' : 'column',
                gap: { xs: 1.5, sm: 2, md: 3 },
                flexWrap: 'wrap',
                height: isMobile ? 'auto' : '80%',
                mt: { xs: 6, sm: 7, md: 8 },
                alignContent: 'flex-start',
                justifyContent: isMobile ? 'center' : isTablet ? 'flex-start' : 'flex-start',
                width: '100%',
                pb: { xs: 12, sm: 10, md: 0 }, // More padding on mobile for taskbar
                px: { xs: 1, sm: 2, md: 0 },
                maxHeight: isMobile ? 'calc(100vh - 180px)' : 'auto',
                overflowY: isMobile ? 'auto' : 'visible',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
            }}>
                <SortableContext
                    items={items.map(i => i.id)}
                    strategy={rectSortingStrategy}
                >
                    {items.map((item) => (
                        <SortableIcon
                            key={item.id}
                            id={item.id}
                            icon={item.icon}
                            label={item.label}
                            path={item.path}
                        />
                    ))}
                </SortableContext>
            </Box>

            <DragOverlay dropAnimation={dropAnimation}>
                {activeId && activeItem ? (
                    <DesktopIcon
                        id={activeItem.id}
                        label={activeItem.label}
                        icon={activeItem.icon}
                        path={activeItem.path}
                    />
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};

export default DesktopGrid;
